import React from 'react';
import { Copy } from 'lucide-react';
import { Button } from '@roger/ui-components';
import type {
  ImportedAllergyLineResponse,
  ImportedMedicationLineResponse,
} from '../../services/api';
import { formatDateInPT, formatDateToMMDDYYYY } from '../../utils/dateUtils';

const Field: React.FC<{
  label: string;
  value?: string | null;
  /** Renders a copy affordance beside the value when provided. */
  onCopy?: () => void;
  copyLabel?: string;
  valueClassName?: string;
}> = ({ label, value, onCopy, copyLabel, valueClassName }) =>
  value ? (
    <div>
      <dt className='text-xs uppercase tracking-wide text-muted-foreground'>
        {label}
      </dt>
      <dd className='flex items-start gap-1 text-sm'>
        <span className={valueClassName}>{value}</span>
        {onCopy && (
          <Button
            type='button'
            variant='ghost'
            size='sm'
            className='h-5 w-5 shrink-0 p-0'
            onClick={onCopy}
            aria-label={copyLabel}
            title={copyLabel}
          >
            <Copy className='h-3.5 w-3.5' />
          </Button>
        )}
      </dd>
    </div>
  ) : null;

/**
 * The "Was" side of reconciliation: the Kinnser line exactly as it arrived.
 *
 * **The order string is the headline, and that is a clinical decision.**
 * Kinnser's own decomposition into dose and sig is best-effort and
 * demonstrably lossy — hold parameters, taper instructions and the
 * prescriber's exact wording live in the sentence and nowhere else. So the
 * sentence is set large and first, and the parsed fields sit under it labelled
 * as Kinnser's parse rather than as facts. A clinician deciding what this line
 * means should be reading the sentence.
 *
 * Everything Kinnser sent is shown, including its own codes. They are evidence
 * for the person ruling on the line, never an identity Roger adopts.
 */
export const ImportedMedicationWasPanel: React.FC<{
  line: ImportedMedicationLineResponse;
  /**
   * Copies Kinnser's verbatim sig into the entry side. A deliberate click,
   * not a pre-fill: the clinician adopts the wording; nothing arrives unasked.
   */
  onCopySig?: (sig: string) => void;
}> = ({ line, onCopySig }) => (
  <div className='space-y-3' data-testid='was-panel'>
    <div>
      <div className='text-xs uppercase tracking-wide text-muted-foreground'>
        Order as Kinnser holds it
      </div>
      <p
        className='mt-1 whitespace-pre-line rounded-md border border-amber-200 bg-amber-50 p-3 text-base font-medium leading-relaxed text-amber-900 break-words'
        data-testid='was-order-string'
      >
        {line.order_string ||
          line.name ||
          line.title ||
          'Kinnser sent no order text for this line.'}
      </p>
    </div>
    <dl className='grid grid-cols-2 gap-3'>
      <Field label='Name' value={line.name} />
      {/* Discontinued reads red: the accident this prevents is a stopped drug
          being carried forward as a fresh active medication. */}
      <Field
        label='Status'
        value={line.status}
        valueClassName={
          line.status?.trim().toLowerCase() === 'discontinued'
            ? 'font-semibold text-destructive'
            : undefined
        }
      />
      {/* Labelled as Kinnser's parse, not as the dose. The distinction is the
          whole reason the sentence above is set larger than these. */}
      <Field label="Kinnser's dose" value={line.dose} />
      <Field
        label="Kinnser's sig"
        value={line.sig}
        onCopy={onCopySig && line.sig ? () => onCopySig(line.sig!) : undefined}
        copyLabel='Copy sig into frequency / instructions'
      />
      <Field label='Start date' value={formatDateToMMDDYYYY(line.start_date)} />
      <Field
        label='Discontinue date'
        value={formatDateToMMDDYYYY(line.discontinue_date)}
      />
      <Field label='Drug class' value={line.drug_class} />
      <Field label='GPI' value={line.gpi} />
    </dl>
    <p className='text-xs text-muted-foreground'>
      Imported {formatDateInPT(line.imported_at)}.
    </p>
  </div>
);

/**
 * The "Was" side for an allergy line.
 *
 * `is_freeform` is surfaced because it is the single best signal of how much
 * the wording can be trusted: a clerk typing into a box and a clerk picking
 * from Kinnser's list produce the same-looking string with very different
 * reliability.
 */
export const ImportedAllergyWasPanel: React.FC<{
  line: ImportedAllergyLineResponse;
}> = ({ line }) => (
  <div className='space-y-3' data-testid='was-panel'>
    <div>
      <div className='text-xs uppercase tracking-wide text-muted-foreground'>
        Allergen as Kinnser holds it
      </div>
      <p
        className='mt-1 whitespace-pre-line rounded-md border border-amber-200 bg-amber-50 p-3 text-base font-medium leading-relaxed text-amber-900 break-words'
        data-testid='was-order-string'
      >
        {line.allergen || 'Kinnser sent no allergen text for this line.'}
      </p>
    </div>
    <dl className='grid grid-cols-2 gap-3'>
      <Field label='Reaction' value={line.reaction} />
      <Field label='Type' value={line.allergy_type} />
      <Field
        label='Entry'
        value={
          line.is_freeform === null || line.is_freeform === undefined
            ? undefined
            : line.is_freeform
              ? 'Typed free-hand in Kinnser'
              : "Picked from Kinnser's list"
        }
      />
    </dl>
    <p className='text-xs text-muted-foreground'>
      Imported {formatDateInPT(line.imported_at)}.
    </p>
  </div>
);
